import React, { useState, useEffect } from "react";
import { List, ChevronRight, ChevronDown } from "lucide-react";

interface TOCItem {
  id: string;
  title: string;
  level: number;
}

interface TableOfContentsProps {
  className?: string;
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ className = "" }) => {
  const [tocItems, setTocItems] = useState<TOCItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    // Find all headings with IDs
    const headings = document.querySelectorAll('h2[id], h3[id], h4[id]');
    const items: TOCItem[] = Array.from(headings).map((heading) => ({
      id: heading.id,
      title: heading.textContent || "",
      level: parseInt(heading.tagName.charAt(1))
    }));
    setTocItems(items);

    // Intersection Observer for active section tracking
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-10% 0px -80% 0px" }
    );

    headings.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (tocItems.length === 0) return null;

  return (
    <div className={`bg-slate-50 border border-slate-200 rounded-lg p-6 ${className}`}>
      <div 
        className="flex items-center justify-between cursor-pointer mb-4"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h3 className="font-bold text-lg flex items-center">
          <List className="h-5 w-5 mr-2 text-[#8932c8]" />
          Table of Contents
        </h3>
        {isCollapsed ? (
          <ChevronRight className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </div>
      
      {!isCollapsed && (
        <nav className="space-y-2">
          {tocItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`block w-full text-left py-2 px-3 rounded transition-colors ${
                activeId === item.id
                  ? 'bg-[#8932c8] text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              } ${item.level === 3 ? 'ml-4' : ''} ${item.level === 4 ? 'ml-8' : ''}`}
            >
              <span className="text-sm">{item.title}</span>
            </button>
          ))}
        </nav>
      )}
    </div>
  );
};

export default TableOfContents;